import React, { useState } from 'react';

const TimeTravelHistoryGame = ({ gameData, onComplete }) => {
    const [currentEraIndex, setCurrentEraIndex] = useState(0);
    const [score, setScore] = useState(0);
    const [showQuestion, setShowQuestion] = useState(false);
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [feedback, setFeedback] = useState(null);

    const currentEra = gameData?.eras?.[currentEraIndex];

    const handleAnswer = (answer) => {
        if (feedback) return;
        setSelectedAnswer(answer);
        const isCorrect = answer === currentEra.correct_answer;

        if (isCorrect) {
            const pointsToAdd = gameData.points_per_era || 20;
            const newScore = score + pointsToAdd;
            setScore(newScore);
            setFeedback({ type: 'success', message: 'ISTORINIS TIKSLUMAS! 🎉' });
            if (navigator.vibrate) navigator.vibrate([100, 50, 100]);

            setTimeout(() => {
                nextEra(newScore);
            }, 4000);
        } else {
            setFeedback({ type: 'error', message: 'EPOCHOS KLAIDA! ❌' });
            if (navigator.vibrate) navigator.vibrate([50, 50, 50]);
            setTimeout(() => {
                setFeedback(null);
                setSelectedAnswer(null);
            }, 2000);
        }
    };

    const nextEra = (currentScore) => {
        if (currentEraIndex < gameData.eras.length - 1) {
            setCurrentEraIndex(prev => prev + 1);
            setShowQuestion(false);
            setSelectedAnswer(null);
            setFeedback(null);
            if (navigator.vibrate) navigator.vibrate(50); // Quantum Jump Vibration
        } else {
            if (navigator.vibrate) navigator.vibrate([200, 100, 200, 100, 300]);
            onComplete?.(currentScore);
        }
    };

    if (!gameData || !gameData.eras || !currentEra) {
        return (
            <div className="premium-glass rounded-xl md:rounded-[3rem] p-4 md:p-12 text-center border-amber-100 animate-pulse bg-white">
                <div className="text-lg md:text-4xl md:text-[100px] mb-8">🚀</div>
                <h3 className="text-base md:text-3xl font-black text-amber-900 tracking-tighter uppercase">Kraunamas Chronosferos branduolys...</h3>
            </div>
        );
    }

    const progress = (currentEraIndex / gameData.eras.length) * 100;

    return (
        <div className="space-y-3 md:space-y-8 animate-slide-up select-none">
            {/* Header / Chrono HUD */}
            <div className="premium-glass rounded-lg md:rounded-[2.5rem] p-3 md:p-8 border-white/40 shadow-2xl relative overflow-hidden bg-amber-50">
                <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500/5 rounded-full blur-[80px] pointer-events-none"></div>
                <div className="flex flex-col md:flex-row items-center justify-between gap-2 md:p-6 relative z-10">
                    <div className="flex items-center gap-2 md:p-6">
                        <div className="w-20 h-20 bg-amber-100 rounded-3xl flex items-center justify-center text-lg md:text-4xl shadow-inner border-2 border-amber-200 animate-float">
                            🚀
                        </div>
                        <div>
                            <h3 className="text-lg md:text-4xl font-black text-slate-800 tracking-tighter leading-tight uppercase">
                                Laiko <span className="text-amber-600">Keliautojas</span>
                            </h3>
                            <p className="text-slate-500 font-bold italic font-serif">
                                Naršyk per epochas ir atskleisk didžiausias istorijos paslaptis!
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-2 md:p-6">
                        <div className="bg-white/50 px-8 py-4 rounded-[2rem] border-2 border-white shadow-inner">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-2">Išmintis</p>
                            <p className="text-base md:text-3xl font-black text-amber-600 leading-none">⭐ {score}</p>
                        </div>
                        <div className="bg-white/50 px-8 py-4 rounded-[2rem] border-2 border-white shadow-inner text-center min-w-[120px]">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-2">Epocha</p>
                            <p className="text-base md:text-3xl font-black text-slate-800 leading-none">{currentEraIndex + 1}/{gameData.eras.length}</p>
                        </div>
                    </div>
                </div>

                <div className="mt-8 w-full bg-slate-200/50 rounded-full h-4 p-1 shadow-inner overflow-hidden border-2 border-slate-300">
                    <div 
                        className="h-full bg-gradient-to-r from-amber-400 via-orange-500 to-yellow-600 rounded-full transition-all duration-1000 shadow-[0_0_20px_rgba(245,158,11,0.4)]"
                        style={{ width: `${progress}%` }}
                    />
                </div>
            </div>

            {/* Tactical Timeline Ribbon */}
            <div className="premium-glass rounded-xl md:rounded-[3rem] p-3 md:p-8 shadow-2xl border-white/60 bg-white/40 backdrop-blur-xl overflow-x-auto custom-scrollbar flex items-center gap-4 md:p-12 lg:gap-4 md:p-16 px-12 lg:px-6 md:px-20 relative">
                <div className="absolute inset-y-0 left-0 w-12 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none" />
                <div className="absolute inset-y-0 right-0 w-12 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none" />
                
                {gameData.eras.map((era, idx) => {
                    const isCurrent = idx === currentEraIndex;
                    const isPast = idx < currentEraIndex;
                    return (
                        <div key={idx} className={`flex flex-col items-center gap-4 shrink-0 transition-all duration-700 ${isCurrent ? 'scale-125 z-20' : 'opacity-30 scale-90'}`}>
                            <div className={`
                                w-20 h-20 rounded-[2rem] flex items-center justify-center text-lg md:text-4xl shadow-2xl border-4 transform-gpu transition-all
                                ${isCurrent ? 'bg-amber-500 text-white border-amber-200 animate-pulse' : isPast ? 'bg-emerald-500 text-white border-emerald-200' : 'bg-white text-slate-300 border-slate-100'}
                            `}>
                                {isPast ? '✓' : era.emoji}
                            </div>
                            <div className={`text-[12px] font-black uppercase tracking-[0.2em] leading-none ${isCurrent ? 'text-amber-600' : 'text-slate-400'}`}>{era.year}</div>
                        </div>
                    );
                })}
            </div>

            {/* Chronosphere Interaction Stage */}
            <div className="premium-glass rounded-2xl md:rounded-[4rem] p-4 md:p-10 lg:p-4 md:p-20 shadow-2xl border-white/60 bg-white/40 backdrop-blur-xl relative overflow-hidden flex flex-col items-center min-h-[50vh] justify-center text-center">
                <div className="absolute top-0 right-0 p-4 md:p-12 text-[250px] opacity-[0.03] select-none pointer-events-none rotate-12">🏛️</div>
                <div className="absolute bottom-0 left-0 p-4 md:p-12 text-[250px] opacity-[0.03] select-none pointer-events-none -rotate-12 text-amber-600">🦖</div>
                
                <div className="w-full max-w-5xl relative z-10">
                    {!showQuestion ? (
                        <div className="animate-slide-up space-y-4 md:space-y-12">
                            <div className="space-y-3 md:space-y-8">
                                <div className="text-2xl md:text-6xl md:text-[180px] drop-shadow-[0_40px_80px_rgba(245,158,11,0.4)] animate-float mb-8">{currentEra.emoji}</div>
                                <div className="space-y-2">
                                    <h2 className="text-7xl lg:text-9xl font-black text-amber-600 tracking-tighter italic font-serif leading-none drop-shadow-sm">{currentEra.year}</h2>
                                    <h3 className="text-lg md:text-4xl lg:text-xl md:text-5xl font-black text-slate-900 tracking-tight leading-tight uppercase italic">{currentEra.event}</h3>
                                </div>
                            </div>
                            
                            <div className="bg-white rounded-2xl md:rounded-[4rem] p-4 md:p-12 lg:p-4 md:p-16 shadow-[0_40px_80px_rgba(245,158,11,0.1)] border-4 border-amber-50 relative group transition-all duration-500 hover:scale-[1.02] max-w-4xl mx-auto">
                                <div className="absolute top-0 left-0 p-4 md:p-10 text-8xl text-amber-200 opacity-20 select-none font-serif leading-none group-hover:scale-125 transition-transform">“</div>
                                <p className="text-base md:text-3xl lg:text-lg md:text-4xl font-bold text-slate-800 leading-relaxed italic font-serif relative z-10 px-10 tracking-tight">
                                    {currentEra.description}
                                </p>
                            </div>

                            <button
                                onClick={() => { if (navigator.vibrate) navigator.vibrate(30); setShowQuestion(true); }}
                                className="premium-button-3d group w-full max-w-2xl py-12 bg-gradient-to-r from-amber-500 via-orange-500 to-yellow-600 text-white rounded-[3.5rem] font-black text-lg md:text-4xl shadow-[0_30px_60px_rgba(245,158,11,0.3)] transition-all border-b-[12px] border-amber-800 uppercase tracking-[0.2em] relative overflow-hidden"
                            >
                                <span className="relative z-10 flex items-center justify-center gap-2 md:p-6">
                                    ATSAKYTI Į KLAUSIMĄ <span className="text-xl md:text-5xl animate-bounce">➡️</span>
                                </span>
                                <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity" />
                            </button>
                        </div>
                    ) : (
                        <div className="animate-slide-up space-y-4 md:space-y-12">
                            <div className="space-y-6">
                                <div className="inline-block bg-amber-600 text-white px-10 py-4 rounded-full text-[10px] font-black uppercase tracking-[0.5em] shadow-xl animate-pop-in">TEMPORALINIS KLAUSIMAS</div>
                                <h3 className="text-lg md:text-4xl lg:text-2xl md:text-6xl font-black text-slate-950 tracking-tighter leading-tight px-6 italic font-serif">
                                    {currentEra.question}
                                </h3>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:p-8 w-full max-w-5xl mx-auto">
                                {currentEra.options.map((option, idx) => {
                                    const isSelected = selectedAnswer === option;
                                    const isCorrect = option === currentEra.correct_answer;
                                    
                                    return (
                                        <button
                                            key={idx}
                                            onClick={() => !selectedAnswer && handleAnswer(option)}
                                            disabled={selectedAnswer !== null}
                                            className={`
                                                relative p-4 md:p-10 lg:p-4 md:p-12 rounded-xl md:rounded-[3rem] text-base md:text-3xl font-black transition-all duration-500 border-b-[12px] transform-gpu
                                                ${isSelected 
                                                    ? isCorrect 
                                                        ? 'bg-emerald-500 text-white border-emerald-800 shadow-[0_20px_50px_rgba(16,185,129,0.5)] scale-105 z-10' 
                                                        : 'bg-rose-500 text-white border-rose-800 shadow-[0_20px_50px_rgba(244,63,94,0.5)] scale-95 opacity-80'
                                                    : selectedAnswer && isCorrect 
                                                        ? 'bg-emerald-500 text-white border-emerald-800 scale-105 shadow-2xl' 
                                                        : 'bg-white text-slate-800 border-slate-200 hover:bg-amber-50 hover:scale-105 active:translate-y-4 active:border-b-0'}
                                                disabled:cursor-default shadow-xl
                                            `}
                                        >
                                            <div className="flex items-center justify-center gap-4">
                                                {isSelected && isCorrect && <span className="text-lg md:text-4xl animate-bounce">🌟</span>}
                                                {isSelected && !isCorrect && <span className="text-lg md:text-4xl animate-shake">🤔</span>}
                                                <span className="uppercase tracking-tight leading-tight">{option}</span>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Immersive Era Success Overlay */}
            {feedback && feedback.type === 'success' && (
                <div className="fixed inset-0 flex items-center justify-center z-[1000] p-2 md:p-6">
                    <div className="absolute inset-0 bg-slate-900/70 backdrop-blur-3xl animate-fade-in" />
                    <div className="max-w-3xl w-full p-4 md:p-20 rounded-3xl md:rounded-[5rem] bg-white shadow-[0_60px_120px_rgba(0,0,0,0.6)] relative animate-pop-in border-[16px] border-amber-500 text-center">
                        <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-48 h-48 bg-white rounded-full flex items-center justify-center text-xl md:text-5xl md:text-[120px] shadow-2xl border-8 border-inherit animate-bounce">
                            🕰️
                        </div>
                        
                        <div className="space-y-4 md:space-y-12 pt-14">
                            <h2 className="text-7xl font-black uppercase tracking-tighter text-amber-600 leading-none italic font-serif">
                                ISTORIJOS MEISTRAS!
                            </h2>
                            <div className="p-4 md:p-12 rounded-2xl md:rounded-[4rem] bg-amber-50 border-4 border-amber-100 shadow-inner">
                                <p className="text-lg md:text-4xl font-bold text-slate-800 leading-relaxed font-serif italic px-6 tracking-tight">
                                    "Puiku! Tavo žinios apie epochą <span className="text-amber-600 font-black">{currentEra.year}</span> yra nepriekaištingos. Tęskime kelionę per laiką!"
                                </p>
                            </div>
                            <div className="flex items-center justify-center gap-2 md:p-6">
                                <div className="w-5 h-5 bg-slate-200 rounded-full animate-bounce"></div>
                                <div className="w-5 h-5 bg-slate-200 rounded-full animate-bounce [animation-delay:0.2s]"></div>
                                <div className="w-5 h-5 bg-slate-200 rounded-full animate-bounce [animation-delay:0.4s]"></div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TimeTravelHistoryGame;
